export default class EventEmitter<EventMap extends Record<string, unknown> = Record<string, unknown>> {
	listeners: {
		[EventName in keyof EventMap]?: Array<Listener<EventMap, EventName>>;
	} = {};
	on<EventName extends keyof EventMap>(
		event: EventName,
		callback: (...payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]) => void,
	) {
		return new Listener(event, callback, this);
	}
	once<EventName extends keyof EventMap>(
		event: EventName,
		callback: (...payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]) => void,
	) {
		const handle = new Listener(
			event,
			(...payload) => {
				callback(...payload);
				handle.remove();
			},
			this,
		);
		return handle;
	}
	emit<EventName extends keyof EventMap>(
		event: EventName,
		...payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]
	) {
		if (this.listeners[event]) {
			this.listeners[event].forEach((listener) => {
				listener.call(payload);
			});
		}
	}
}

export class Listener<EventMap extends Record<string, unknown>, EventName extends keyof EventMap = keyof EventMap> {
	event: EventName;
	callback: (...payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]) => void;
	controller: EventEmitter<EventMap>;

	constructor(
		event: EventName,
		callback: (...payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]) => void,
		controller: EventEmitter<EventMap>,
	) {
		this.event = event;
		this.callback = callback;
		this.controller = controller;
		if (!controller.listeners[event]) {
			controller.listeners[event] = [];
		}
		controller.listeners[event].push(this as Listener<EventMap, EventName>);
	}

	remove() {
		const listeners = this.controller.listeners[this.event];
		if (listeners) {
			listeners.splice(listeners.indexOf(this as Listener<EventMap, EventName>), 1);
		}
	}

	call(payload: undefined extends EventMap[EventName] ? [EventMap[EventName]?] : [EventMap[EventName]]): void {
		this.callback(...payload);
	}
}
